import { useState, useEffect } from 'react';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import useLocalStorageState from 'use-local-storage-state';
import { setDelogueApiToken } from 'lib/api/utils';
import { type AuthContext, AuthTokenContext } from 'features/auth/hooks/authContext';
import { SelectedUserContext, SELECTED_USER_ID_KEY } from 'features/auth/hooks/selectedUserContext';
import { useLoginUsers } from 'features/auth/hooks/federatedAuthHooks';

const AuthTokenProvider = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [authContext, setAuthContext] = useState<AuthContext>({
    isLoading: true,
    isAuthenticated: false,
    token: null,
  });

  useEffect(() => {
    if (!isAuthenticated || isLoading) {
      setDelogueApiToken(null);
      setAuthContext({ isLoading, isAuthenticated, token: null });
      return;
    }
    const fetchToken = async () => {
      try {
        const accessToken = await getAccessTokenSilently();
        setDelogueApiToken(accessToken);
        setAuthContext({ isLoading, isAuthenticated, token: accessToken });
      } catch {
        setDelogueApiToken(null);
        setAuthContext({ isLoading: false, isAuthenticated: false, token: null });
      }
    };
    fetchToken();
  }, [isAuthenticated, isLoading, getAccessTokenSilently]);

  return <AuthTokenContext.Provider value={authContext}>{children}</AuthTokenContext.Provider>;
};

const SelectedUserProvider = ({ children }: { children: React.ReactNode }) => {
  const [selectedUserId, setSelectedUserId, { removeItem }] = useLocalStorageState<string | null>(
    SELECTED_USER_ID_KEY,
    { defaultValue: null }
  );

  const { data: users } = useLoginUsers();

  useEffect(() => {
    if (!users || !selectedUserId) return;
    const exists = users.some(u => u.id === selectedUserId);
    if (!exists) removeItem();
  }, [users, selectedUserId, removeItem]);

  const selectUser = (userId: string) => setSelectedUserId(userId);
  const clearSelectedUser = () => removeItem();

  return (
    <SelectedUserContext.Provider value={{ selectedUserId, selectUser, clearSelectedUser }}>
      {children}
    </SelectedUserContext.Provider>
  );
};

type AuthProviderProps = {
  children: React.ReactNode;
};

export const AuthProviderFederated = ({ children }: AuthProviderProps) => {
  return (
    <Auth0Provider
      domain={import.meta.env.VITE_AUTH0_DOMAIN}
      clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
      }}
    >
      <AuthTokenProvider>
        <SelectedUserProvider>{children}</SelectedUserProvider>
      </AuthTokenProvider>
    </Auth0Provider>
  );
};
